import { For, Match, Show, Switch, createEffect, createMemo, onMount, splitProps } from "solid-js";
import type { ATable, ATableCell, ATableScreen, ATableEmptyKey } from "./_model";
import { global } from ":shared/app";

// TODO[done]: turn base table style to grid, add it to options and force control flow to use grid
// TODO[pending]: check if it's better to draw the grid column by column instead of row by row on the desktop.
// TODO[pending]: add gap to grid
// TODO[pending]: on the phone, check if the moving width control to grid-template-columns with 2 columns, is better for mentality and features
// TODO[pending]: turn headers option to optional and accept any items object and turn it to table
export default function Table<T extends any[]>(props: ATable<T>) {
  const [local, others] = splitProps(props, [
    "_items",
    "_headers",
    "_emptyKey",
    "_fallback",
    "_class",
    "_containers",
    "_overlays",
  ]);
  const _empty_key: ATableEmptyKey = {
    desktop: typeof local._emptyKey === "string" ? local._emptyKey : local._emptyKey?.desktop ?? "",
    phone: typeof local._emptyKey === "string" ? local._emptyKey : local._emptyKey?.desktop ?? "",
  };
  const refs: Record<string, HTMLDivElement> = {};
  const $breakpoint = global.store.hooks.dom.useBreakpoints();
  const $screen: () => ATableScreen = createMemo(() => {
    return $breakpoint()?.misc?.md?.widthIsLess ? "phone" : "desktop";
  });
  // headers and cells pairs
  const $hcp = createMemo(() => {
    // TODO: look for user given keys from local.items passed if local.headers is not given
    if (local._headers == undefined) {
      return undefined;
    }
    const result = [] as [string, ATableCell<T>][];
    for (const header in local._headers) {
      const value = local._headers[header];
      if (header == undefined || value == undefined) {
        console.warn(`table: detected null header definition in <${header ?? "UNKNOWN HEADER"}> !!`);
        continue;
      }
      result.push([header, value]);
    }
    return result;
  });
  const $items = createMemo(() => {
    const hcp = $hcp();
    const result = {
      exist: true, // assume items exist
      array: typeof local._items === "function" ? local._items() : local._items,
    };
    if (local._items == undefined || local._items.length <= 0 || hcp == undefined || hcp.length <= 0) {
      result.exist = false;
    }
    return result;
  });

  function getItemMeta(props: { screen: ATableScreen; item: T[number]; itemIndex: () => number }) {
    const { screen, item, itemIndex } = props;
    const result = {
      container:
        typeof local._containers?.items === "function"
          ? local._containers?.items({ screen, item, itemIndex })
          : local._containers?.items,
      overlay:
        typeof local._overlays?.items === "function"
          ? local._overlays?.items({ screen, item, itemIndex })
          : local._overlays?.items,
    };

    return result;
  }

  function getCellMeta(props: {
    screen: ATableScreen;
    header: string;
    cell: ATableCell<T>;
    hcpIndex: () => number;
    item?: T[number];
    itemIndex?: () => number;
  }) {
    const { screen, item, itemIndex, header, cell, hcpIndex } = props;
    const empty_key: ATableEmptyKey = {
      desktop: typeof cell.emptyKey === "string" ? cell.emptyKey : cell?.emptyKey?.desktop ?? _empty_key.desktop,
      phone: typeof cell.emptyKey === "string" ? cell.emptyKey : cell?.emptyKey?.desktop ?? _empty_key.desktop,
    };
    const result = {
      hide: typeof cell?.hide === "function" ? cell.hide({ screen, item }) : cell.hide,
      hideHeader: screen === "desktop" ? false : cell?.hideHeader?.phone ?? false,
      empty_key: screen === "desktop" ? empty_key.desktop : empty_key.phone,
      overlay_below: cell?.overlayBelow == undefined ? false : cell?.overlayBelow,
      value: null,
      container: {
        header: undefined,
        cell: undefined,
      },
    };
    if (result.hide == undefined) {
      result.hide = false;
    }

    // the item itself is undefined, there's no point setting up signals or further details
    if (item == undefined) {
      return result;
    }

    if (screen === "desktop") {
      result.container.header =
        typeof cell.containers?.desktop?.header === "function"
          ? cell.containers.desktop.header({ screen, item, itemIndex })
          : cell.containers?.desktop?.header;
      result.container.cell =
        typeof cell.containers?.desktop?.cell === "function"
          ? cell.containers.desktop.cell({ screen, item, itemIndex })
          : cell.containers?.desktop?.cell;
    } else {
      result.container.header =
        typeof cell.containers?.phone?.header === "function"
          ? cell.containers.phone.header({ screen, item, itemIndex })
          : cell.containers?.phone?.header;
      result.container.cell =
        typeof cell.containers?.phone?.cell === "function"
          ? cell.containers.phone.cell({ screen, item, itemIndex })
          : cell.containers?.phone?.cell;
    }

    if (typeof cell.key === "function") {
      result.value = cell.key({ screen, item, itemIndex, emptyKey: result.empty_key });
    } else if (cell.key === "INDEX") {
      result.value = itemIndex?.() ?? "UNKNOWN INDEX"; // don't know if this is evenr possible; not having an itemIndex
    } else if (!(cell.key in item) && !(header in item)) {
      console.warn(
        "table: item key for <",
        header,
        `> is not found, please use <${header}>.key to define it appropriately!`
      );
      result.value = `ITEM KEY NOT FOUND ${(cell.key as string) ?? header}`;
    } else {
      result.value = item[cell.key ?? header] ?? result.empty_key;
    }

    return result;
  }

  // onMount(() => {
  //   for (const r in refs) {
  //     console.log(r, " :: ", refs[r].getBoundingClientRect());
  //   }
  // });

  return (
    //_ overflow works for all devices the same with an exception for mobile where overflow-x has to be stopped
    <section {...others} class={(others?.class ?? "") + " " + "relative flex flex-col w-full h-full overflow-"}>
      <Switch>
        {/* phone and tablet */}
        <Match when={$screen() === "phone"}>
          <Show when={$items().exist} fallback={local._fallback ?? "table has no content"}>
            {/* we can't use table with tr as immediate children in solidjs, so we have to change hierarchy :( */}
            <section
              class={
                (local._class?.phone?.wrapper ?? "") +
                " " +
                "relative flex flex-col w-full h-full  scrollbar text-black"
              }
            >
              <For each={$items().array}>
                {(item, itemIndex) => {
                  const $meta = createMemo(() => getItemMeta({ screen: "phone", item, itemIndex }));
                  return (
                    // single card
                    <div
                      {...($meta()?.container as any)}
                      style={{
                        ...$meta()?.container?.style,
                        display: "grid",
                        "grid-template-columns": "max-content 1fr",
                      }}
                      class={
                        (local._class?.phone?.card ?? "") +
                        " " +
                        ($meta()?.container?.class ?? "") +
                        " " +
                        "!relative w-full h-fit"
                      }
                    >
                      {$meta()?.overlay != undefined && (
                        <p class="!absolute left-0 right-0 bottom-0 top-0 w-full h-full overflow-visible !z-1">
                          {$meta()?.overlay}
                        </p>
                      )}
                      <For each={$hcp()}>
                        {([header, cell], hcpIndex) => {
                          const $meta = createMemo(() =>
                            getCellMeta({ screen: "phone", header, cell, hcpIndex, item, itemIndex })
                          );
                          const $icon = createMemo(() => {
                            if (cell.icon == undefined) {
                              return undefined;
                            }
                            if (typeof cell.icon === "object" && "phone" in cell.icon) {
                              return cell.icon?.phone;
                            }
                            return cell.icon as JSX.Element;
                          });

                          return (
                            <Show when={!$meta()?.hide}>
                              {!$meta()?.hideHeader && (
                                <p
                                  {...$meta()?.container.header}
                                  style={{
                                    ...$meta()?.container.header?.style,
                                  }}
                                  class={
                                    (cell?.class?.phone?.headerAndCell ?? "") +
                                    " " +
                                    (local._class?.phone?.header ?? "") +
                                    " " +
                                    (cell?.class?.phone?.header ?? "") +
                                    " " +
                                    ($meta()?.container?.header?.class ?? "") +
                                    " " +
                                    "text-start !z-0"
                                  }
                                >
                                  {$icon()}
                                  <span
                                    class={
                                      (cell?.class?.phone?.headerTitle ?? "") +
                                      " " +
                                      (local._class?.phone?.headerTitle ?? "")
                                    }
                                  >
                                    {header}
                                  </span>
                                </p>
                              )}
                              <p
                                {...$meta()?.container.cell}
                                style={{
                                  ...$meta()?.container.cell?.style,
                                }}
                                class={
                                  (cell?.class?.phone?.headerAndCell ?? "") +
                                  " " +
                                  (local._class?.phone?.cell ?? "") +
                                  " " +
                                  (cell?.class?.phone?.cell ?? "") +
                                  " " +
                                  ($meta()?.container?.cell?.class ?? "") +
                                  " " +
                                  ($meta()?.hideHeader ? "!col-span-2" : "") +
                                  " " +
                                  ($meta()?.overlay_below ? "!z-2" : "!z-0") +
                                  " " +
                                  "!relative overflow-auto w-full text-start"
                                }
                              >
                                {$meta()?.value}
                              </p>
                            </Show>
                          );
                        }}
                      </For>
                    </div>
                  );
                }}
              </For>
            </section>
          </Show>
        </Match>
        <Match when={true}>
          {/* wrapper */}
          <section
            class={
              (local._class?.desktop?.table ?? "") +
              " " +
              "relative flex flex-col w-full h-full !overflow-auto scrollbar"
            }
          >
            {/* headers wrapper */}
            <div
              class={
                (local._class?.desktop?.headersWrapper ?? "") +
                " " +
                "!sticky left-0 !top-0 min-w-full w-fit min-h-20px flex flex-row !z-2 overflow-hidden"
              }
            >
              {/* headers */}
              <For each={$hcp()}>
                {([header, cell], hcpIndex) => {
                  const $meta = createMemo(() => getCellMeta({ screen: "desktop", header, cell, hcpIndex }));
                  const $icon = createMemo(() => {
                    if (cell.icon == undefined) {
                      return undefined;
                    }
                    if (typeof cell.icon === "object" && "desktop" in cell.icon) {
                      return cell.icon?.desktop;
                    }
                    return cell.icon as JSX.Element;
                  });
                  onMount(() => {
                    const r = refs[hcpIndex()];
                    if (r == undefined) {
                      return;
                    }
                    const rect = r.getBoundingClientRect();
                    r.style.width = rect.width + "px";
                    r.style.minWidth = rect.width + "px";
                    r.style.maxWidth = rect.width + "px";
                  });
                  return (
                    // single header
                    <Show when={!$meta()?.hide}>
                      <div
                        {...$meta()?.container.header}
                        ref={(ref) => {
                          if (!ref) {
                            return;
                          }
                          refs[hcpIndex()] = ref;
                          // console.log(header, " :: ", ref?.getBoundingClientRect());
                        }}
                        style={{
                          ...$meta()?.container.header?.style,
                        }}
                        class={
                          (local._class?.desktop?.headersAndCells ?? "") +
                          " " +
                          (cell?.class?.desktop?.headerAndCell ?? "") +
                          " " +
                          (local._class?.desktop?.headers ?? "") +
                          " " +
                          (cell?.class?.desktop?.header ?? "") +
                          " " +
                          ($meta()?.container?.header?.class ?? "") +
                          " " +
                          "relative w-full text-start flex-1 !z-1 overflow-hidden"
                        }
                      >
                        {$icon()}
                        <span
                          class={
                            (cell?.class?.desktop?.headerTitle ?? "") + " " + (local._class?.desktop?.headerTitle ?? "")
                          }
                        >
                          {header}
                        </span>
                      </div>
                    </Show>
                  );
                }}
              </For>
            </div>
            {/* rows wrapper */}
            <div
              class={
                (local._class?.desktop?.rowsWrapper ?? "") +
                " " +
                "flex flex-col w-full !z-0 text-black" +
                " " +
                (!$items().exist ? "!flex" : "")
              }
            >
              <Show
                when={$items().exist}
                fallback={
                  <div class="flex w-full h-full">
                    {local._fallback ?? "table has no content"}
                    {/* <div class="">{local._fallback ?? "table has no content"}</div> */}
                  </div>
                }
              >
                {/* rows */}
                <For each={$items().array}>
                  {(item, itemIndex) => {
                    const $meta = createMemo(() => getItemMeta({ screen: "desktop", item, itemIndex }));
                    return (
                      // single row
                      <div
                        {...$meta()?.container}
                        style={{
                          ...$meta()?.container?.style,
                        }}
                        class={
                          (local._class?.desktop?.rows ?? "") +
                          " " +
                          ($meta()?.container?.class ?? "") +
                          " " +
                          "relative flex flex-row min-w-fit w-fit min-h-20px"
                        }
                      >
                        {/* overlay */}
                        {$meta()?.overlay != undefined && (
                          <div class="!absolute left-0 top-0 bottom-0 w-full h-full !z-1">{$meta()?.overlay}</div>
                        )}
                        {/* cells */}
                        <For each={$hcp()}>
                          {([header, cell], hcpIndex) => {
                            const $meta = createMemo(() =>
                              getCellMeta({ screen: "desktop", header, cell, hcpIndex, item, itemIndex })
                            );
                            let ref: HTMLDivElement = undefined;
                            onMount(() => {
                              const r = refs[hcpIndex()];
                              if (r == undefined) {
                                return;
                              }
                              const rect = r.getBoundingClientRect();
                              ref.style.width = rect.width + "px";
                              ref.style.minWidth = rect.width + "px";
                              ref.style.maxWidth = rect.width + "px";
                              // TODO: add on r or header resize event, to resize this cell as well
                            });

                            return (
                              // cell value
                              <Show when={!$meta()?.hide}>
                                <div
                                  {...$meta()?.container.cell}
                                  ref={ref}
                                  style={{
                                    ...$meta()?.container.cell?.style,
                                  }}
                                  class={
                                    (local._class?.desktop?.headersAndCells ?? "") +
                                    " " +
                                    (cell?.class?.desktop?.headerAndCell ?? "") +
                                    " " +
                                    (local._class?.desktop?.cells ?? "") +
                                    " " +
                                    (cell?.class?.desktop?.cell ?? "") +
                                    " " +
                                    ($meta()?.container?.cell?.class ?? "") +
                                    " " +
                                    ($meta()?.overlay_below ? "!z-2" : "!z-0") +
                                    " " +
                                    "relative flex w-full items-center text-start overflow-auto"
                                  }
                                >
                                  {$meta()?.value}
                                </div>
                              </Show>
                            );
                          }}
                        </For>
                      </div>
                    );
                  }}
                </For>
              </Show>
            </div>
          </section>
        </Match>
      </Switch>
    </section>
  );
}
